<template>
  <div class="relative h-dvh flex flex-col bg-pitch-black text-total-white">
    <!-- Global Header -->
    <div
      class="flex w-full items-center justify-between px-4 py-3 border-b border-prime-golden bg-pitch-black"
    >
      <a href="/" class="text-prime-golden text-base font-medium font-avenir-medium">
        <img
          class="w-24 h-24 -my-8 -ml-[10px]"
          src="@/assets/images/mason-logo.svg"
          alt="Mason Logo"
        />
      </a>
    </div>
    <div
      v-if="isLoading"
      class="absolute inset-0 z-10 flex items-center justify-center bg-black bg-opacity-40"
    ></div>

    <!-- Main Scrolling Container -->
    <div ref="scrollContainer" class="flex-1 flex flex-col overflow-y-auto pb-4">
      <!-- STEP 1: File Upload / Manual Link -->
      <section v-if="currentStep === STEP.UPLOAD" class="relative flex flex-col flex-1">
        <div class="sticky top-0 z-50 bg-pitch-black p-4">
          <StepHeader title="" @back="handleBack" />
        </div>

        <div class="flex flex-col flex-1 space-y-6 justify-center mt-2">
          <button
            type="button"
            @click="openFilePicker"
            class="border-2 border-dashed border-prime-golden-200 bg-prime-golden-100 text-prime-golden rounded-lg p-6 text-center mx-8 text-lg font-bold"
          >
            Upload Contract
            <br />

            <span class="text-total-white-700 text-sm font-medium leading-1px">
              Create your project with Mason Project Agent
            </span>
          </button>
          <input
            ref="fileInputRef"
            type="file"
            class="hidden"
            accept=".pdf,.doc,.docx,image/*"
            @change="onFileSelect"
          />
          <div class="text-center">
            <button
              @click="handleCreateManually"
              class="underline text-prime-golden-800 text-sm underline-offset-4 mt-4"
            >
              Create your project manually
            </button>
          </div>

          <div v-if="selectedFile" class="text-center text-sm mt-2">
            Selected file: <strong>{{ selectedFile.name }}</strong>
          </div>
        </div>
      </section>

      <!-- STEP 2: Promo Code -->
      <section v-else-if="currentStep === STEP.PROMO" class="relative">
        <div class="z-50 bg-pitch-black p-4">
          <StepHeader title="Subscribe to Mason Project Agent" @back="handleBack" />
        </div>
        <div class="flex flex-col space-y-4 mt-2 max-w-md mx-auto p-4">
          <input
            v-model="promoCode"
            type="text"
            placeholder="Enter your promo code"
            class="w-full p-3 rounded-lg bg-dark-grey text-total-white focus:outline-none focus:border-prime-golden"
          />
          <div class="flex items-center gap-2">
            <!-- Show spinner only when loading -->
            <img
              v-if="isLoading"
              src="@/assets/images/spinner.svg"
              alt="Loading..."
              class="w-6 h-6"
            />
            <button
              @click="submitPromoCode"
              class="bg-prime-golden flex-1 text-pitch-black py-2 rounded-full font-bold flex items-center justify-center gap-2"
              :disabled="isLoading"
              :class="{ 'opacity-50 pointer-events-none': isLoading }"
            >
              <span>{{ isLoading ? "Loading..." : "Enter" }}</span>
            </button>
          </div>
          <div class="text-sm text-center mt-4">
            <button
              class="underline font-semibold text-prime-golden"
              @click="goToContactUs"
            >
              Don't have a promo code? Contact Us!
            </button>
          </div>
        </div>
      </section>

      <!-- STEP 3: Review Homeowner -->
      <!-- Step 3: Review Homeowner (like "Edit Contact" style) -->
      <section v-else-if="currentStep === STEP.HOMEOWNER" class="relative">
        <!-- Basic header (not sticky) -->
        <div class="p-4 bg-pitch-black">
          <StepHeader
            :title="!existingContactHomeowner ? 'Review Homeowner' : 'Select Homeowner'"
            :disabled="isLoading"
            :hideBack="!existingContactHomeowner ? true : false"
            @back="
              !existingContactHomeowner
                ? handleHomeownerBack
                : (existingContactHomeowner = false)
            "
          />
        </div>

        <!-- "Step 1 of 2" text, or similar -->
        <div class="text-sm text-total-white-700 px-4 mt-2">
          <p>Step 1 of 2</p>
        </div>

        <!-- Some descriptive text about editing the homeowner, etc. -->
        <div
          v-if="!existingContactHomeowner"
          class="text-sm text-total-white-700 px-4 pt-2 pb-2"
        >
          <p>Please review and confirm the homeowner details below.</p>
          <p class="mt-2">
            You may also
            <span
              class="underline cursor-pointer w-fit p-0 text-prime-golden mr-1"
              @click="selectExistingContact"
            >
              select an existing contact</span
            >to be the homeowner or skip this step to assign no homeowner to the project
          </p>
        </div>

        <!-- If we are NOT selecting an existing contact, show the "Edit-like" form -->
        <div v-if="!existingContactHomeowner" class="px-4 pt-4">
          <!-- Profile image + "upload image" text -->
          <div
            class="flex flex-col items-center gap-2 text-prime-golden underline underline-offset-4 justify-center mb-6"
          >
            <div
              class="w-20 h-20 rounded-full flex items-center justify-center bg-light-grey-translucent-dark border border-light-grey-translucent-dark relative"
            >
              <template v-if="homeownerForm.profile_image_url">
                <img
                  :src="homeownerForm.profile_image_url"
                  alt="Profile Image"
                  class="w-full h-full object-cover rounded-full border z-2"
                />
                <!-- Optional "remove image" icon if you want to let them remove it -->

                <div
                  class="absolute top-0 right-0 bg-gray-500 w-6 h-6 rounded-full p-1 z-10 flex items-center justify-center border border-light-grey-translucent-dark"
                  @click="removeHomeownerImage"
                >
                  <i class="fa fa-times text-white text-sm"></i>
                </div>
              </template>
              <template v-else>
                <!-- Placeholder icon -->
                <i class="fa-solid fa-user text-6xl text-pitch-black"></i>
              </template>
            </div>
            <span class="relative overflow-hidden inline-block cursor-pointer text-sm">
              <!-- Visible Upload Text -->
              <span class="text-prime-golden rounded-full"> upload image </span>
              <!-- Hidden Input -->
              <input
                class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                type="file"
                accept="image/*"
                @change="onHomeownerImageChange"
              />
            </span>
          </div>

          <!-- The homeowner form fields (like your "Edit Contact" fields) -->
          <form @submit.prevent="saveHomeowner" class="space-y-6 max-w-xl mx-auto">
            <!-- First Name (Required) -->
            <div>
              <label class="block text-left mb-1">First Name *</label>
              <input
                v-model="homeownerForm.first_name"
                type="text"
                class="w-full p-3 bg-dark-grey rounded-md"
                placeholder="Enter first name"
                @input="homeownerErrors.first_name = ''"
              />
              <div
                v-if="homeownerErrors.first_name"
                class="text-deep-rust text-sm text-left mt-1"
              >
                {{ homeownerErrors.first_name }}
              </div>
            </div>

            <!-- Last Name (Optional) -->
            <div>
              <label class="block text-left mb-1">Last Name</label>
              <input
                v-model="homeownerForm.last_name"
                type="text"
                class="w-full p-3 bg-dark-grey rounded-md"
                placeholder="Enter last name"
                @input="homeownerErrors.last_name = ''"
              />
              <div
                v-if="homeownerErrors.last_name"
                class="text-deep-rust text-sm text-left mt-1"
              >
                {{ homeownerErrors.last_name }}
              </div>
            </div>

            <!-- Email (Optional) -->
            <div>
              <label class="block text-left mb-1">Email</label>
              <input
                v-model="homeownerForm.email"
                type="email"
                class="w-full p-3 bg-dark-grey rounded-md"
                placeholder="Homeowner email"
                @input="homeownerErrors.email = ''"
              />
              <div
                v-if="homeownerErrors.email"
                class="text-deep-rust text-sm text-left mt-1"
              >
                {{ homeownerErrors.email }}
              </div>
            </div>

            <!-- Phone using PhoneInput (Optional) -->
            <div>
              <PhoneInput
                id="phone"
                v-model="homeownerForm.phone"
                label="Phone Number"
                placeholder="Enter phone number"
                @input="homeownerErrors.phone = ''"
              />
              <div
                v-if="homeownerErrors.phone"
                class="text-deep-rust text-sm text-left mt-1"
              >
                {{ homeownerErrors.phone }}
              </div>
            </div>

            <!-- Role is hidden in the UI (hardcoded as "homeowner") -->

            <div class="flex justify-end gap-4 pt-6">
              <div class="flex items-center gap-2">
                <img
                  v-if="isLoading"
                  src="@/assets/images/spinner.svg"
                  alt="Loading..."
                  class="w-4 h-4"
                />
                <button
                  type="submit"
                  class="bg-prime-golden text-pitch-black font-semibold py-1 px-3 rounded-lg"
                  :disabled="isLoading"
                  :class="{ 'opacity-50 pointer-events-none': isLoading }"
                >
                  {{ isLoading ? "Saving..." : "Save" }}
                </button>
              </div>
              <button
                type="button"
                @click="skipHomeowner"
                class="bg-gray-500 text-total-white font-semibold py-1 px-3 rounded-lg"
                :disabled="isLoading"
                :class="{ 'opacity-50 pointer-events-none': isLoading }"
              >
                Skip
              </button>
            </div>
          </form>
        </div>

        <!-- Step 3.5: "Select existing homeowner" sub-step -->
        <div v-else class="flex flex-col max-w-xl mx-auto p-4">
          <!-- The dropdown to pick an existing contact as homeowner -->
          <CustomDropdown
            v-model="form.homeownerId"
            :options="contactList"
            placeholder="Select a homeowner"
          />
          <ErrorMessage :message="errors.homeownerId" />

          <div class="flex justify-end gap-4 pt-4 text-sm">
            <button
              type="button"
              @click="skipHomeowner"
              class="bg-prime-golden text-sm text-pitch-black font-semibold py-1 px-3 rounded-lg"
            >
              Continue
            </button>
          </div>
        </div>

        <!-- Cropper modal for the homeowner image -->
      </section>
      <div v-if="showHomeownerCropper" class="cropper-modal">
        <cropper
          ref="homeownerCropperRef"
          class="max-h-[80dvh]"
          :src="imageToCrop"
          :aspect-ratio="1"
          :view-mode="1"
          @cropend="onHomeownerCropEnd"
          :movable="false"
          :zoomable="false"
          :rotatable="false"
          :scalable="false"
        />
        <div class="flex gap-2 mt-4">
          <button
            @click="confirmHomeownerCrop"
            class="bg-prime-golden text-pitch-black font-semibold py-1 px-3 rounded-lg"
          >
            Crop Image
          </button>
          <button
            @click="showHomeownerCropper = false"
            class="bg-gray-500 text-total-white font-semibold py-1 px-3 rounded-lg"
          >
            Cancel
          </button>
        </div>
      </div>

      <!-- STEP 4: Review / Create Project -->
      <section v-else-if="currentStep === STEP.DETAILS" class="relative">
        <div class="z-50 bg-pitch-black p-4">
          <StepHeader
            :title="isManual ? 'Enter Project Details' : 'Review Project Details'"
            @back="handleBack"
          />
        </div>
        <div
          v-if="aiAssisted || !isManual"
          class="text-sm text-total-white-700 px-4 mt-[-2]"
        >
          <p>Step 2 of 2</p>
        </div>
        <div
          v-if="aiAssisted || !isManual"
          class="text-sm text-total-white-700 px-4 pt-2 pb-8"
        >
          <p>Please review and confirm the project details below.</p>
        </div>
        <form
          @submit.prevent="onCreateProject"
          class="space-y-6 mt-2 p-4 max-w-xl mx-auto pb-8"
        >
          <div>
            <label for="address_1" class="flex justify-start text-sm font-medium mb-1">
              Address Line 1 *
            </label>
            <input
              id="address_1"
              v-model="form.address_1"
              @input="errors.address_1 = ''"
              type="text"
              placeholder="Enter address"
              class="w-full p-3 rounded-lg bg-dark-grey text-total-white"
            />
            <ErrorMessage :message="errors.address_1" />
          </div>

          <div>
            <label for="address_2" class="flex justify-start text-sm font-medium mb-1">
              Address Line 2
            </label>
            <input
              id="address_2"
              v-model="form.address_2"
              @input="errors.address_2 = ''"
              type="text"
              placeholder="Enter address line 2"
              class="w-full p-3 rounded-lg bg-dark-grey text-total-white"
            />
            <ErrorMessage :message="errors.address_2" />
          </div>

          <div>
            <label for="scope" class="flex justify-start text-sm font-medium mb-1">
              Scope *
            </label>
            <input
              id="scope"
              v-model="form.scope"
              @input="errors.scope = ''"
              type="text"
              placeholder="Enter project scope"
              class="w-full p-3 rounded-lg bg-dark-grey text-total-white"
            />
            <ErrorMessage :message="errors.scope" />
          </div>

          <div>
            <label for="start_date" class="flex justify-start text-sm font-medium mb-1">
              Start Date (MM/DD/YYYY) *
            </label>
            <VueDatePicker
              v-model="form.start_date"
              @change="handleDateChange"
              :enable-time-picker="false"
              dark
              aria-placeholder="MM/DD/YYYY"
              class="w-full p-1 rounded-lg bg-dark-grey text-total-white"
            />

            <ErrorMessage :message="errors.start_date" />
          </div>

          <div>
            <label for="country" class="flex justify-start text-sm font-medium mb-1">
              Country *
            </label>
            <CustomDropdown
              :options="countryList"
              v-model="form.country"
              allowOtherValues="true"
              placeholder="Select country"
            />
            <ErrorMessage :message="errors.country" />
          </div>

          <div>
            <label for="state" class="flex justify-start text-sm font-medium mb-1">
              State *
            </label>
            <CustomDropdown
              :options="stateList"
              v-model="form.state"
              allowOtherValues="true"
              placeholder="Select state"
            />
            <ErrorMessage :message="errors.state" />
          </div>

          <div>
            <label for="city" class="flex justify-start text-sm font-medium mb-1">
              City *
            </label>
            <CustomDropdown
              :options="cityList"
              v-model="form.city"
              allowOtherValues="true"
              placeholder="Select city"
            />
            <ErrorMessage :message="errors.city" />
          </div>

          <div>
            <label for="zip" class="flex justify-start text-sm font-medium mb-1">
              Zip Code *
            </label>
            <input
              id="zip"
              v-model="form.zip"
              @input="errors.zip = ''"
              type="text"
              placeholder="Enter zip code"
              class="w-full p-3 rounded-lg bg-dark-grey text-total-white"
            />
            <ErrorMessage :message="errors.zip" />
          </div>

          <div>
            <label for="Homeowner" class="flex justify-start text-sm font-medium mb-1">
              Homeowner
            </label>
            <CustomDropdown
              v-model="form.homeownerId"
              :options="contactList"
              placeholder="Select a homeowner"
            />
            <ErrorMessage :message="errors.homeownerId" />
          </div>

          <div v-if="isAdmin" class="flex items-center gap-2">
            <input
              type="checkbox"
              v-model="form.is_simulation"
              id="is_simulation"
              class="hidden"
            />
            <div
              @click="form.is_simulation = !form.is_simulation"
              :class="[
                'w-5 h-5 rounded flex items-center justify-center border cursor-pointer',
                form.is_simulation
                  ? 'bg-dark-grey border-gray-700'
                  : 'bg-gray-600 border-gray-700',
              ]"
            >
              <svg
                v-if="form.is_simulation"
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 text-prime-golden"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="3"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <label for="is_simulation" class="ml-1 text-sm font-medium">Simulation</label>
          </div>

          <div class="flex justify-end gap-4 my-4">
            <div class="flex items-center text-sm gap-2">
              <img
                v-if="isLoading"
                src="@/assets/images/spinner.svg"
                alt="Loading"
                class="inline-block w-4 h-4"
              />
              <button
                type="submit"
                :disabled="isLoading"
                class="bg-prime-golden text-pitch-black font-semibold py-1 w-20 px-3 rounded-lg flex items-center justify-center gap-1"
                :class="{ 'opacity-50 pointer-events-none': isLoading }"
              >
                <span>{{ isLoading ? "Saving..." : "Create" }}</span>
              </button>
            </div>
            <button
              type="button"
              @click="cancelProject"
              class="bg-gray-500 text-total-white text-sm font-semibold py-1 w-20 px-3 rounded-lg"
              :disabled="isLoading"
              :class="{ 'opacity-50 pointer-events-none': isLoading }"
            >
              Cancel
            </button>
          </div>
        </form>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { Country, State, City } from "country-state-city";

import StepHeader from "@/components/StepHeader.vue";
import ErrorMessage from "@/components/ErrorMessage.vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import CustomDropdown from "@/components/CustomDropdown.vue";
import "@vuepic/vue-datepicker/dist/main.css";
import Cropper from "vue-cropperjs";
import "cropperjs/dist/cropper.css";

import { useUserStore } from "@/stores/user";
import { useProjectStore } from "@/stores/project";
import { useContactsStore } from "@/stores/contact";
import PhoneInput from "@/components/PhoneInput.vue";

// Steps
const STEP = {
  UPLOAD: 1,
  PROMO: 2,
  HOMEOWNER: 3,
  DETAILS: 4,
};

const router = useRouter();
const route = useRoute();
const toast = useToast();

const userStore = useUserStore();
const projectStore = useProjectStore();
const contactsStore = useContactsStore();

const currentStep = ref(STEP.UPLOAD);
const isManual = ref(false);
const selectedFile = ref(null);
const fileInputRef = ref(null);
// Flag: true if a contract was uploaded and user not in manual mode
const aiAssisted = computed(() => !isManual.value && selectedFile.value !== null);

// The main project form
const form = ref({
  start_date: "",
  scope: "",
  address_1: "",
  address_2: "",
  city: "",
  state: "",
  zip: "",
  country: "",
  projectManagerId: "",
  homeownerId: "",
  is_simulation: false,
});

// Homeowner sub‐form
const homeownerForm = ref({
  id: null,
  first_name: "",
  last_name: "",
  email: "john@example.com",
  phone: "555-1234",
  role: "Homeowner",
  profile_image_url: "",
});

// So we know if we’re using “Select existing contact” mode
const existingContactHomeowner = ref(false);

const errors = ref({});
const isLoading = ref(false);
const errorMessage = ref("");

const promoCode = ref("");

// For watchers
const countries = ref(Country.getAllCountries());
const states = ref([]);
const cities = ref([]);
const contacts = ref([]);

// “+ New Contact” plus each existing contact
const contactList = computed(() => [
  {
    label: "+ New Contact",
    value: "new",
    onClick: (e) => {
      // Prevent the dropdown from selecting "new" as a normal option:
      e.preventDefault();

      // Make sure the wizard goes to the "Review Homeowner" sub-step
      // and clears all homeowner fields:
      form.value.homeownerId = "";
      homeownerForm.value = {
        id: null,
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        role: "Homeowner",
        profile_image_url: "",
      };
      existingContactHomeowner.value = false;
      currentStep.value = STEP.HOMEOWNER;
    },
  },
  // Include the contact’s role in the label:
  ...contacts.value
    .filter((c) => c.role === "Homeowner" || c.role === "")
    .map((c) => ({
      label: `${c.first_name} ${c.last_name}`,
      sublabel: c.role,
      value: c.id,
    })),
]);

const isAdmin = computed(() => userStore.profile?.account_type === "admin");

// We’ll convert each Country/State/City into { label, value }
const countryList = computed(() =>
  countries.value.map((c) => ({ label: c.name, value: c.name }))
);
// Computed dropdown for states – add current state if not found (case‑insensitive)
const stateList = computed(() => {
  const opts = states.value.map((s) => ({ label: s.name, value: s.name }));
  if (
    form.value.state &&
    !opts.some((o) => o.value.toLowerCase() === form.value.state.toLowerCase())
  ) {
    opts.unshift({ label: form.value.state, value: form.value.state });
  }
  return opts;
});

// Computed dropdown for cities – add current city if not found (case‑insensitive)
const cityList = computed(() => {
  const opts = cities.value.map((city) => ({ label: city.name, value: city.name }));
  if (
    form.value.city &&
    !opts.some((o) => o.value.toLowerCase() === form.value.city.toLowerCase())
  ) {
    opts.unshift({ label: form.value.city, value: form.value.city });
  }
  return opts;
});

// Watcher for country – only reset states and cities if country changes
watch(
  () => form.value.country,
  (newCountry, oldCountry) => {
    // Convert "USA" (or any case variation) to "United States"
    if (newCountry && newCountry.trim().toLowerCase() === "usa") {
      form.value.country = "United States";
      newCountry = "United States";
    }
    errors.value.country = "";
    // Only update if the country has really changed (case-insensitive)
    if (
      !oldCountry ||
      newCountry.trim().toLowerCase() !== oldCountry.trim().toLowerCase()
    ) {
      // Do not forcibly clear form.state and form.city if they already have a value.
      // Instead, update the states array based on the new country.
      const cObj = countries.value.find(
        (c) => c.name.toLowerCase() === newCountry.trim().toLowerCase()
      );
      if (cObj) {
        states.value = State.getStatesOfCountry(cObj.isoCode);
        // Optionally, you can trigger the state watcher manually if form.state exists:
        if (form.value.state) {
          form.value.state = form.value.state; // triggers state watcher
        }
      } else {
        states.value = [];
      }
    }
  },
  { immediate: true }
);

watch(
  () => form.value.state,
  (newState) => {
    errors.value.state = "";
    cities.value = []; // clear city list on new state
    if (!newState || !form.value.country) return;

    const cObj = countries.value.find(
      (c) => c.name.toLowerCase() === form.value.country.trim().toLowerCase()
    );

    // Try matching by full state name (case-insensitive)
    let matchedState = states.value.find(
      (s) => s.name.toLowerCase() === newState.trim().toLowerCase()
    );

    // If not found and the input is 2 characters long, try matching by ISO code.
    if (!matchedState && newState.trim().length === 2) {
      matchedState = states.value.find(
        (s) => s.isoCode.toLowerCase() === newState.trim().toLowerCase()
      );
    }

    if (matchedState && cObj) {
      // Only update the cities list based on the matched state.
      cities.value = City.getCitiesOfState(cObj.isoCode, matchedState.isoCode);
    } else {
      cities.value = [];
    }
  },
  { immediate: true }
);

// Watcher for city – perform case‑insensitive matching and update if needed
watch(
  () => form.value.city,
  (newCity) => {
    errors.value.city = "";
    if (!newCity || !form.value.country || !form.value.state) return;

    const cObj = countries.value.find(
      (c) => c.name.toLowerCase() === form.value.country.trim().toLowerCase()
    );
    let matchedState = states.value.find(
      (s) => s.name.toLowerCase() === form.value.state.trim().toLowerCase()
    );
    if (!matchedState && form.value.state.trim().length === 2) {
      matchedState = states.value.find(
        (s) => s.isoCode.toLowerCase() === form.value.state.trim().toLowerCase()
      );
    }
    if (cObj && matchedState) {
      // Update cities list based on current country and state
      cities.value = City.getCitiesOfState(cObj.isoCode, matchedState.isoCode);
      // Try to find a matching city (case-insensitive)
      const matchedCity = cities.value.find(
        (city) => city.name.toLowerCase() === newCity.trim().toLowerCase()
      );
      if (
        matchedCity &&
        form.value.city.trim().toLowerCase() !== matchedCity.name.toLowerCase()
      ) {
        form.value.city = matchedCity.name;
      }
      // If no match is found, the computed cityList will include the raw value.
    }
  },
  { immediate: true }
);
function convertExtractedState(extractedState, country) {
  if (!extractedState) return extractedState;
  const trimmed = extractedState.trim();
  // Only try to convert if the value is exactly two letters and a country is provided.
  if (trimmed.length === 2 && country) {
    // Normalize the country value: if "USA" is passed, convert it to "United States"
    let normalizedCountry = country.trim();
    if (normalizedCountry.toLowerCase() === "usa") {
      normalizedCountry = "United States";
    }
    // Find the country object by matching its name or ISO code (case-insensitive)
    const cObj = countries.value.find(
      (c) =>
        c.name.toLowerCase() === normalizedCountry.toLowerCase() ||
        c.isoCode.toLowerCase() === normalizedCountry.toLowerCase()
    );
    if (cObj) {
      // Get the list of states for that country.
      const countryStates = State.getStatesOfCountry(cObj.isoCode);
      // Try to find a state matching the two-letter abbreviation (case-insensitive)
      const found = countryStates.find(
        (s) => s.isoCode.toLowerCase() === trimmed.toLowerCase()
      );
      if (found) {
        return found.name;
      }
    }
  }
  return extractedState;
}

onMounted(async () => {
  await contactsStore.fetchAllContacts();
  contacts.value = contactsStore.contacts;
  if (!userStore.profile) {
    await userStore.fetchUserProfile();
  }
  countries.value = Country.getAllCountries();
});

// If we have an interim project from route ?step=3
onMounted(async () => {
  const forcedStep = Number(route.query.step);
  if (forcedStep === 3 && projectStore.interimProject) {
    currentStep.value = STEP.HOMEOWNER;
    const jsonInf = projectStore.interimProject.json_inferences;
    if (jsonInf) {
      interimProjectId.value = projectStore.interimProject.project_id;
      if (jsonInf.homeowner) {
        homeownerForm.value = {
          ...homeownerForm.value,
          ...jsonInf.homeowner,
        };
      }
      if (jsonInf.project) {
        let startDateStr = "";
        if (jsonInf.project.start_date) {
          startDateStr = formatDateToMMDDYYYY(jsonInf.project.start_date);
        }
        let extractedState = jsonInf.project.state || "";
        // Convert the extracted state if it is a two-letter abbreviation.
        const fullState = convertExtractedState(extractedState, jsonInf.project.country);
        form.value = {
          ...form.value,
          ...jsonInf.project,
          start_date: startDateStr,
          state: fullState,
        };
        await nextTick();
      }
    }
  }
  if (!form.value.country || form.value.country.trim() === "") {
    form.value.country = "United States";
  }
  if (!form.value.state || form.value.state.trim() === "") {
    form.value.state = "California";
  }
});

function formatDateToMMDDYYYY(dateString) {
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}
const formatDateToYYYYMMDD = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const handleDateChange = (date) => {
  form.value.start_date = formatDateToYYYYMMDD(date);
};

function handleBack() {
  if (currentStep.value === STEP.UPLOAD) {
    router.push({ name: "ProjectsPage" });
    return;
  }
  if (currentStep.value === STEP.DETAILS && isManual.value) {
    currentStep.value = STEP.UPLOAD;
    return;
  }
  currentStep.value--;
}
function openFilePicker() {
  fileInputRef.value.click();
}
function onFileSelect(e) {
  const files = e.target.files;
  if (!files || !files.length) return;
  selectedFile.value = files[0];
  currentStep.value = STEP.PROMO;
}
function handleCreateManually() {
  isManual.value = true;
  currentStep.value = STEP.DETAILS;
}
function goToContactUs() {
  router.push({ name: "Contact" });
}

const interimProjectId = ref(null);

async function submitPromoCode() {
  if (!promoCode.value.trim()) {
    toast.error("Please enter a promo code!");
    return;
  }
  isLoading.value = true;
  if (selectedFile.value) {
    const fd = new FormData();
    fd.append("files", selectedFile.value);
    fd.append("promo_code", promoCode.value.trim());
    try {
      const response = await projectStore.createProjectWithFiles(fd);
      if (response.interim_project.json_inferences) {
        if (response.interim_project.json_inferences.homeowner) {
          homeownerForm.value = {
            ...homeownerForm.value,
            ...response.interim_project.json_inferences.homeowner,
          };
        }
        if (response.interim_project.json_inferences.project) {
          let filledDate = "";
          if (response.interim_project.json_inferences.project.start_date) {
            filledDate = formatDateToMMDDYYYY(
              response.interim_project.json_inferences.project.start_date
            );
          }
          let extractedState =
            response.interim_project.json_inferences.project.state || "";
          // Convert only if the extracted state is a 2-letter abbreviation.
          const fullState = convertExtractedState(
            extractedState,
            response.interim_project.json_inferences.project.country
          );

          form.value = {
            ...form.value,
            ...response.interim_project.json_inferences.project,
            start_date: filledDate,
            state: fullState,
          };

          await nextTick();
        }
      }
      interimProjectId.value = response?.interim_project?.project_id;
      if (!form.value.country || form.value.country.trim() === "") {
        form.value.country = "United States";
      }
      if (!form.value.state || form.value.state.trim() === "") {
        form.value.state = "California";
      }
      currentStep.value = STEP.HOMEOWNER;
    } catch (err) {
      console.error(err);
      if (err.response && err.response.status === 500) {
        toast.error("Error extracting PDF contents");
      } else {
        toast.error("Invalid promo code");
      }
    } finally {
      isLoading.value = false;
    }
  } else {
    currentStep.value = STEP.HOMEOWNER;
  }
}

function selectExistingContact() {
  existingContactHomeowner.value = true;
}

const homeownerImageInput = ref(null);

const showHomeownerCropper = ref(false);
const homeownerCropperRef = ref(null);
const imageToCrop = ref(null);
const croppedHomeownerFile = ref(null);

function onHomeownerImageChange(e) {
  const file = e.target.files?.[0];
  if (file) {
    const reader = new FileReader();
    reader.onload = (evt) => {
      imageToCrop.value = evt.target.result;
      showHomeownerCropper.value = true;
    };
    reader.readAsDataURL(file);
  }
}

function confirmHomeownerCrop() {
  const cropper = homeownerCropperRef.value;
  if (cropper) {
    const croppedCanvas = cropper.getCroppedCanvas();
    croppedCanvas.toBlob((blob) => {
      if (blob) {
        croppedHomeownerFile.value = new File([blob], "cropped-homeowner.png", {
          type: "image/png",
        });
      }
    });
    homeownerForm.value.profile_image_url = croppedCanvas.toDataURL();
    showHomeownerCropper.value = false;
  }
}

function onHomeownerCropEnd() {
  // Optional: handle crop end event if needed
}

function removeHomeownerImage() {
  homeownerForm.value.profile_image_url = "";
  croppedHomeownerFile.value = null;
}

// Homeowner form validation: only first name is required; last name and phone are optional.
function validateHomeownerForm() {
  homeownerErrors.value = {};

  // First Name (Required)
  if (!homeownerForm.value.first_name) {
    homeownerErrors.value.first_name = "First name is required.";
  }

  // Last Name: Optional – no required validation

  // Phone: Validate only if provided
  if (homeownerForm.value.phone) {
    if (!/^\+?\d+$/.test(homeownerForm.value.phone.replace(/\s/g, ""))) {
      homeownerErrors.value.phone =
        "Phone number must contain only digits, with an optional '+' at the start.";
    } else {
      const digits = homeownerForm.value.phone.replace(/\s/g, "").replace(/^\+/, "");
      if (digits.length < 7 || digits.length > 14) {
        homeownerErrors.value.phone = "Phone number must be 7 to 14 digits long.";
      }
    }
  }

  // Email: Optional, but if provided, validate format
  if (homeownerForm.value.email) {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([^<>()[\]\\.,;:\s@"]+\.)+[^<>()[\]\\.,;:\s@"]{2,})$/i;
    if (!re.test(homeownerForm.value.email)) {
      homeownerErrors.value.email = "Please enter a valid email address.";
    }
  }

  return Object.keys(homeownerErrors.value).length === 0;
}

const homeownerErrors = ref({});

homeownerForm.value.role = "Homeowner";

async function saveHomeowner() {
  try {
    isLoading.value = true;
    if (!validateHomeownerForm()) {
      isLoading.value = false;
      return;
    }

    let savedContact;
    if (homeownerForm.value.id) {
      savedContact = await contactsStore.updateContact(homeownerForm.value.id, {
        ...homeownerForm.value,
        role: "Homeowner",
        profile_image_url: "",
      });
    } else {
      savedContact = await contactsStore.createContact({
        first_name: homeownerForm.value.first_name,
        last_name: homeownerForm.value.last_name,
        email: homeownerForm.value.email,
        phone: homeownerForm.value.phone.replace(/\s/g, ""),
        role: "Homeowner",
      });
    }
    homeownerForm.value.id = savedContact.id;

    if (croppedHomeownerFile.value) {
      await contactsStore.uploadContactImage(savedContact.id, croppedHomeownerFile.value);
    }
    // await contactsStore.fetchAllContacts();
    // contacts.value = contactsStore.contacts;

    form.value.homeownerId = savedContact.id;
    currentStep.value = STEP.DETAILS;
  } catch (err) {
    console.error(err);
    toast.error(
      "Failed to save homeowner. A contact with the provided phone or email might already exist. Please skip this step if this is the case, and select the relevant homeowner in the next step.",
      {
        timeout: 10000,
      }
    );
  } finally {
    isLoading.value = false;
  }
}

function skipHomeowner() {
  currentStep.value = STEP.DETAILS;
}

function cancelProject() {
  router.push({ name: "ProjectsPage" });
}

const scrollContainer = ref(null);

async function onCreateProject() {
  if (!validateForm()) {
    if (scrollContainer.value) {
      scrollContainer.value.scrollTop = 0;
    }
    return;
  }

  isLoading.value = true;
  errorMessage.value = "";
  try {
    const isoDate = form.value.start_date
      ? formatDateToYYYYMMDD(form.value.start_date) + "T00:00:00Z"
      : "";

    // Convert next_milestone_target_date to ISO
    const isoMilestoneDate = form.value.next_milestone_target_date
      ? formatDateToYYYYMMDD(form.value.next_milestone_target_date) + "T00:00:00Z"
      : "";

    // Convert next_action_item_due_date to ISO
    const isoActionDate = form.value.next_action_item_due_date
      ? formatDateToYYYYMMDD(form.value.next_action_item_due_date) + "T00:00:00Z"
      : "";

    const projectData = {
      address_1: form.value.address_1,
      address_2: form.value.address_2,
      city: form.value.city,
      state: form.value.state,
      zip: form.value.zip,
      country: form.value.country,
      start_date: isoDate,
      scope: form.value.scope,
      project_name: form.value.address_1,
      is_simulation: form.value.is_simulation,
      project_manager_id: form.value.projectManagerId,
      ...(form.value.homeownerId ? { homeowner_id: form.value.homeownerId } : {}),
      ...(form.value.next_milestone ? { next_milestone: form.value.next_milestone } : {}),
      ...(isoMilestoneDate ? { next_milestone_target_date: isoMilestoneDate } : {}),
      ...(form.value.next_action_item
        ? { next_action_item: form.value.next_action_item }
        : {}),
      ...(isoActionDate ? { next_action_item_due_date: isoActionDate } : {}),
      ...(form.value.next_action_item_owners
        ? { next_action_item_owners: form.value.next_action_item_owners }
        : {}),
    };
    if (!isManual.value && interimProjectId.value) {
      await projectStore.updateProject(interimProjectId.value, projectData);
    } else {
      const newFormData = new FormData();
      newFormData.append("project_data", JSON.stringify(projectData));
      await projectStore.createProjectWithFiles(newFormData);
    }

    router.push({ name: "ProjectsPage" });
  } catch (err) {
    console.error(err);
    if (err.response && err.response.status === 500) {
      toast.error("A problem occurred while creating the project");
    } else {
      toast.error("Failed to create project.");
    }
    errorMessage.value = "Failed to create project.";
  } finally {
    isLoading.value = false;
  }
}

function validateForm() {
  errors.value = {};

  if (!form.value.start_date) {
    errors.value.start_date = "Start date is required.";
  }
  if (!form.value.scope) errors.value.scope = "Scope is required.";
  if (!form.value.address_1) errors.value.address_1 = "Address Line 1 is required.";
  if (!form.value.country) errors.value.country = "Country is required.";
  if (!form.value.state) errors.value.state = "State is required.";
  if (!form.value.city) errors.value.city = "City is required.";

  if (!form.value.zip) {
    errors.value.zip = "Zip code is required.";
  } else if (!/^[a-zA-Z0-9\s-]+$/.test(form.value.zip)) {
    errors.value.zip = "Please enter a valid zip/postal code.";
  }

  return Object.keys(errors.value).length === 0;
}
</script>

<style scoped>
input[type="date"]::-webkit-calendar-picker-indicator {
  filter: invert(1);
}
.cropper-modal {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: 1;
  height: 100dvh;
}
</style>
